import { useAccount } from '@azure/msal-react';
import {
  Button,
  Dropdown,
  DropdownOption,
  FileUploader,
  RadioButton,
  RadioButtonGroup,
  SystemIcon,
  TextField,
  Typography,
} from '@volvo/vcdk-react';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { Controller, useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IIndustrialForm, IUnitBaseRegistration } from '@/service/vpc/types/IUnitRegistration';
import type { FileUploaderEvent, RadioButtonGroupChangeEvent } from '@volvo/vcdk';
import type { FileUploadItem } from '@volvo/vcdk-react/FileUploader';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import {
  StepEnum,
  type ISegmentData,
  type StepEnum as StepEnumType,
} from '@/components/modals/unit-registration/AddNewUnitModal';
import Stepper, { Step } from '@/components/Stepper';
import useAnalytics from '@/contexts/analytics/useAnalytics';
import { account } from '@/utils';
import { countries } from '@/utils/country';

type Props = {
  origin?: string;
  qrCode?: string;
  unitNotExistInProductCenter: boolean;
  segment: Partial<ISegmentData>;
  defaultValues: Partial<IUnitBaseRegistration>;
  setDefaultValues: Dispatch<SetStateAction<Partial<IUnitBaseRegistration>>>;
  defaultValuesIndustrial: Partial<IIndustrialForm>;
  setDefaultValuesIndustrial: Dispatch<SetStateAction<Partial<IIndustrialForm>>>;
  onCancel: () => void;
  onNext: Dispatch<SetStateAction<StepEnumType>>;
};

type FormData = {
  ownershipFiles: FileUploadItem[];
  qrCode: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  vatNumber: string;
  addressLine: string;
  zipCode: string;
  city: string;
  state: string;
  country: string;
  notificationPreferences: boolean;
};

const IndustrialContactInformationContent: React.FC<Props> = ({
  segment,
  origin,
  qrCode,
  unitNotExistInProductCenter,
  defaultValues,
  setDefaultValues,
  defaultValuesIndustrial,
  setDefaultValuesIndustrial,
  onCancel,
  onNext,
}) => {
  const { t } = useTranslation();
  const { analyticsEvents } = useAnalytics();
  const acc = useAccount();

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      firstName: defaultValuesIndustrial.firstName || (acc ? account.retrieveAccountHolderName(acc).firstName : ''),
      lastName: defaultValuesIndustrial.lastName || (acc ? account.retrieveAccountHolderName(acc).surName : ''),
      email: defaultValuesIndustrial.email || (acc ? account.retrieveEmail(acc) : ''),
      companyName: defaultValues.companyName || '',
      vatNumber: defaultValuesIndustrial.vatNumber || '',
      addressLine: defaultValues.street || '',
      zipCode: defaultValues.postal || '',
      city: defaultValues.city || '',
      state: defaultValues.state || '',
      country: defaultValues.country || '',
      notificationPreferences: defaultValuesIndustrial.notificationPreferences || false,
      qrCode: qrCode || defaultValuesIndustrial.qrCode || '',
      ownershipFiles: defaultValuesIndustrial.ownershipFiles || [],
    },
  });

  const watchedNotificationPreferences = useWatch({
    control: control,
    name: 'notificationPreferences',
  });

  const watchedOwnershipFiles = useWatch({
    control: control,
    name: 'ownershipFiles',
  });

  // Token-prefilled contact fields are locked; only claims the token is missing stay editable.
  const prefilled = useMemo(() => {
    const holderName = acc ? account.retrieveAccountHolderName(acc) : undefined;
    return {
      firstName: !!holderName?.firstName,
      lastName: !!holderName?.surName,
      email: !!(acc && account.retrieveEmail(acc)),
    };
  }, [acc]);

  const onSubmit: SubmitHandler<FormData> = async (value) => {
    // Enter submits the form directly and must not bypass the ownership-files gate.
    if (!(origin === 'VIP' && getValues('qrCode')) && value.ownershipFiles.length === 0) return;
    setDefaultValues((prev) => ({
      ...prev,
      companyName: value.companyName,
      street: value.addressLine,
      city: value.city,
      postal: value.zipCode,
      state: value.state,
      country: value.country,
    }));
    setDefaultValuesIndustrial((prev) => ({
      ...prev,
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      qrCode: value.qrCode || '',
      ownershipFiles: value.ownershipFiles,
      vatNumber: value.vatNumber,
      notificationPreferences: value.notificationPreferences,
    }));
    analyticsEvents.UnitRegistration.contactInformation({
      companyName: value.companyName,
      unitNotExistInProductCenter: unitNotExistInProductCenter,
      mainSegment: segment.mainSegment || '',
      segment: segment.segment || '',
      segmentDetail: segment.segmentDetail || '',
      ownershipConfirmed: Boolean(value.qrCode),
      notificationPreferences: value.notificationPreferences,
    });
    onNext(StepEnum.INDUSTRIAL_UNIT_INFORMATION);
  };

  const handleFilesSelected = (event: FileUploaderEvent) => {
    setValue('ownershipFiles', event.detail.files, { shouldValidate: true });
    setDefaultValuesIndustrial((prev) => ({ ...prev, ownershipFiles: event.detail.files }));
  };

  const handleFilesRemoved = (event: FileUploaderEvent) => {
    const removedIds = new Set(event.detail.files.map((f) => f.id));
    const updatedFiles = getValues('ownershipFiles').filter((f) => !removedIds.has(f.id));
    setValue('ownershipFiles', updatedFiles, { shouldValidate: true });
    setDefaultValuesIndustrial((prev) => ({ ...prev, ownershipFiles: updatedFiles }));
  };

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.title)}</span>
      <div className="flex flex-col gap-8">
        <Stepper>
          <Step stepNo={1} status="completed" label="Search" />
          <Step stepNo={2} status="current" label="Owner info" />
          <Step stepNo={3} status="not-completed" label="Unit" />
        </Stepper>
        <Typography>
          {t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.description)}
        </Typography>
        <form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
          {origin === 'VIP' && getValues('qrCode') ? (
            <div className="border-subtle flex flex-nowrap gap-5 rounded border px-5 py-5">
              <SystemIcon icon="checkmark-circle" className="[&::part(svg)]:text-success" />
              <Typography>
                {t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.proof_of_ownership_qr)}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Controller
                name="ownershipFiles"
                control={control}
                rules={{
                  validate: (files) => files.length > 0,
                }}
                render={({ field }) => (
                  <FileUploader
                    variant="drop-zone"
                    multiple
                    required
                    allowedTypes=".pdf,.png,.jpg,.jpeg,.heic"
                    maxFileSize={3145728}
                    files={field.value}
                    label={t(
                      ($) => $.components.modal.add_new_unit_modal.industrial_contact_information.proof_of_ownership
                    )}
                    helperText={t(
                      ($) =>
                        $.components.modal.add_new_unit_modal.industrial_contact_information
                          .proof_of_ownership_helperText
                    )}
                    onFilesSelected={handleFilesSelected}
                    onFilesRemoved={handleFilesRemoved}
                  >
                    {t(
                      ($) =>
                        $.components.modal.add_new_unit_modal.industrial_contact_information.proof_of_ownership_upload
                    )}
                  </FileUploader>
                )}
              />
            </div>
          )}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col">
              <Typography variant="subtitle1">
                {t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.contact_person)}
              </Typography>
              <Typography variant="body" className="text-subtle">
                {t(
                  ($) => $.components.modal.add_new_unit_modal.industrial_contact_information.contact_person_description
                )}
              </Typography>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    disabled={prefilled.firstName}
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.first_name)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    disabled={prefilled.lastName}
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.last_name)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: true,
                validate: (value) => /^\S+@\S+\.\S+$/.test(value.trim()),
              }}
              render={({ field }) => (
                <TextField
                  required
                  disabled={prefilled.email}
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.email)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col">
              <Typography variant="subtitle1">
                {t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.company_details)}
              </Typography>
              <Typography variant="body" className="text-subtle">
                {t(
                  ($) =>
                    $.components.modal.add_new_unit_modal.industrial_contact_information.company_details_description
                )}
              </Typography>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Controller
                name="companyName"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.company_name)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <Controller
                name="vatNumber"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.vat_number)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </div>
            <Controller
              name="addressLine"
              control={control}
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <TextField
                  required
                  size="medium"
                  label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.address_line)}
                  className="w-full"
                  value={field.value}
                  onInput={(e) => field.onChange(e.detail.value)}
                />
              )}
            />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Controller
                name="zipCode"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.zip_code)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <Controller
                name="city"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value.trim().length > 0,
                }}
                render={({ field }) => (
                  <TextField
                    required
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.city)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.state)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <Controller
                name="country"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Dropdown
                    required
                    size="medium"
                    label={t(($) => $.components.modal.add_new_unit_modal.industrial_contact_information.country)}
                    filterInput
                    className="w-full [&::part(input-wrapper)]:h-10"
                    floatingLabel={false}
                    disableCloseOnSelect
                    value={field.value}
                    onChange={(e) => field.onChange(e.detail.value)}
                  >
                    {countries.map((country) => (
                      <DropdownOption key={country.code} value={country.name}>
                        {country.name}
                      </DropdownOption>
                    ))}
                  </Dropdown>
                )}
              />
            </div>
          </div>
          <div className="">
            <RadioButtonGroup
              label="Notification preferences"
              helperText="For example recalls, safety and upgrades"
              onGroupChange={(e: RadioButtonGroupChangeEvent) => {
                setValue('notificationPreferences', e.detail.value === 'true');
              }}
            >
              <RadioButton
                name="notificationPreferences"
                value="true"
                label="Yes, keep me informed about my unit"
                checked={watchedNotificationPreferences === true}
              />
              <RadioButton
                name="notificationPreferences"
                value="false"
                label="No, I prefer not to receive updates"
                checked={watchedNotificationPreferences === false}
              />
            </RadioButtonGroup>
          </div>
        </form>
      </div>
      <span slot="footer">
        <div className="flex gap-4">
          <Button variant="secondary" onClick={onCancel}>
            {t(($) => $.common.back)}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || (!(origin === 'VIP' && getValues('qrCode')) && watchedOwnershipFiles.length === 0)}
          >
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

export default withComponentErrorBoundary(IndustrialContactInformationContent);
